import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PermissionService } from '../../security/permission.service';
import { AuditCorrectiveActionReportRow } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, formatAuditDate, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-corrective-action-report',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  template: `
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }
    <app-page-shell>
      <app-page-hero eyebrow="Audit Reports" title="Corrective Action Report" subtitle="Export-ready corrective action status, aging, finding, and case data.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" severity="secondary" [outlined]="true"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/actions" label="Actions" icon="pi pi-check-square" severity="secondary" [outlined]="true"></a>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Corrective Actions" subtitle="Action aging and verification status across tenant audit findings." [resultText]="rows.length + ' rows'">
        <p-table [value]="rows" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1420px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Action</th><th>Finding</th><th>Case</th><th>Priority</th><th>Status</th><th>Root Cause</th><th>Effectiveness</th><th>Evidence</th><th>Certificate</th><th>Owner</th><th>Due</th><th>Source</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><a [routerLink]="['/audit/actions', row.actionId]">{{ row.actionNumber || '-' }}</a></td>
              <td><a [routerLink]="['/audit/findings', row.findingId]">{{ row.findingNumber || row.findingTitle || '-' }}</a></td>
              <td><a [routerLink]="['/audit/cases', row.auditCaseId]">{{ row.auditNumber || '-' }}</a></td>
              <td><p-tag [value]="row.priority || '-'" [severity]="statusSeverity(row.priority)"></p-tag></td>
              <td><p-tag [value]="row.actionStatus || '-'" [severity]="statusSeverity(row.actionStatus)"></p-tag></td>
              <td>{{ row.rootCause || '-' }}</td>
              <td>{{ row.effectivenessResult || '-' }}</td>
              <td>
                @if (row.evidenceWeak) {
                  <p-tag value="Weak" severity="danger"></p-tag>
                } @else {
                  <p-tag value="Accepted" severity="success"></p-tag>
                }
              </td>
              <td>{{ row.closureCertificateNumber || '-' }}</td>
              <td>{{ row.ownerUserId || '-' }}</td>
              <td>{{ formatDate(row.dueAt) }} @if (row.overdue) { <strong class="danger">({{ row.daysOverdue }}d overdue)</strong> }</td>
              <td>{{ row.sourceResourceNumber || row.sourceResourceType || row.sourceModule || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="12" class="text-center p-4">No corrective action report rows are available.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`.danger{color:#b91c1c}`]
})
export class AuditCorrectiveActionReportComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  rows: AuditCorrectiveActionReportRow[] = [];
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Actions', value: this.rows.length, tone: 'info' },
      { label: 'Overdue', value: this.rows.filter(row => row.overdue).length, tone: 'danger' },
      { label: 'Rejected', value: this.rows.filter(row => row.actionStatus === 'REJECTED').length, tone: 'warn' },
      { label: 'Verified', value: this.rows.filter(row => row.actionStatus === 'VERIFIED').length, tone: 'success' }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.correctiveActionReport().subscribe({
      next: response => this.rows = response.data ?? [],
      error: () => this.rows = [],
      complete: () => this.loading = false
    });
  }
}
